import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { PreviewPanelToggle } from './preview-panel-toggle'

export type IndexPreviewPinState = {
  open: boolean
  onToggle: () => void
  testId?: string
} | null

type IndexPreviewPinContextValue = {
  pin: IndexPreviewPinState
  setPin: (pin: IndexPreviewPinState) => void
}

const IndexPreviewPinContext = createContext<IndexPreviewPinContextValue>({
  pin: null,
  setPin: () => {},
})

export function IndexPreviewPinProvider({ children }: { children: ReactNode }) {
  const [pin, setPin] = useState<IndexPreviewPinState>(null)
  const value = useMemo(() => ({ pin, setPin }), [pin])
  return (
    <IndexPreviewPinContext.Provider value={value}>
      {children}
    </IndexPreviewPinContext.Provider>
  )
}

export function useIndexPreviewPin(): IndexPreviewPinState {
  return useContext(IndexPreviewPinContext).pin
}

/** Register Continuity preview pin for the chrome strip. Unregisters on unmount. */
export function useRegisterIndexPreviewPin(state: IndexPreviewPinState): void {
  const { setPin } = useContext(IndexPreviewPinContext)
  const open = state?.open ?? false
  const onToggle = state?.onToggle
  const testId = state?.testId
  const active = Boolean(state)

  useEffect(() => {
    if (!active || !onToggle) {
      setPin(null)
      return
    }
    setPin({ open, onToggle, testId })
    return () => setPin(null)
  }, [active, open, onToggle, testId, setPin])
}

/** Renders Continuity preview pin on the chrome strip when an index has registered. */
export function IndexPreviewPinSlot() {
  const pin = useIndexPreviewPin()
  if (!pin) return null
  return (
    <PreviewPanelToggle
      open={pin.open}
      onToggle={pin.onToggle}
      testId={pin.testId}
    />
  )
}
